import { Dispatch } from 'redux';
import ConnectionsController from '../controllers/ConnectionsController';
import commonHandler from '../errorHandlers/common-handler';
import { connectionErrorHandler } from '../errorHandlers/connections-handler';
import { integrationsHandler } from '../errorHandlers/integrations-handler';
import ConnectionModel, { IntegrationTypes } from '../model/ConnectionModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class ConnectionsCRUDActions extends CrudActionContainer {
  retrievedAllByType: string;
  retrieveWidgetConnectionsAction: string;
  retrievedWidgetGoogleConnections: string;
  retrievedWidgetFacebookAudienceConnections: string;
  updateVariableOptionsAction: string;
  invalidateAllAction: string;
  retriveAddedCrmsLeadsSent: string;
  retriveWebhookJsonTemplatesAction: string;

  constructor() {
    super('CONNECTIONS');

    this.retrievedAllByType = this.makeActionKey(
      'RETRIVE_ALL_CONNECTIONS_BY_TYPE',
    );
    this.retrieveWidgetConnectionsAction = this.makeActionKey(
      'RETRIVE_WIDGET_CONNECTIONS',
    );
    this.retrievedWidgetGoogleConnections = this.makeActionKey(
      'RETRIVE_WIDGET_GOOGLE_CONNECTIONS',
    );
    this.retrievedWidgetFacebookAudienceConnections = this.makeActionKey(
      'RETRIEVE_WIDGET_FACEBOOK_AUDIENCE_CONNECTIONS',
    );
    this.updateVariableOptionsAction = this.makeActionKey(
      'UPDATE_VARIABLE_OPTIONS',
    );
    this.invalidateAllAction = this.makeActionKey('INVALIDATE_ALL');
    this.retriveAddedCrmsLeadsSent = this.makeActionKey(
      'RETRIEVE_ADDED_CRMS_LEADS_SENT',
    );
    this.retriveWebhookJsonTemplatesAction = this.makeActionKey(
      'RETRIEVE_WEBHOOK_JSON_TEMPLATES',
    );
  }

  create(facebookPageId: number, connection: Partial<ConnectionModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      try {
        const response: ConnectionModel =
          await ConnectionsController.postWebhook(facebookPageId, connection);
        dispatch(this.retrieved(response));

        return response;
      } catch (error) {
        return commonHandler(dispatch)(
          error,
          integrationsHandler(dispatch, this),
        );
      }
    };
  }

  clone(facebookPageId: number, connectionId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));

      try {
        const response = await ConnectionsController.clone(
          facebookPageId,
          connectionId,
        );
        dispatch(this.retrieved(response));

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveById(facebookPageId: number, connectionId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));

      try {
        const response = await ConnectionsController.getById(
          facebookPageId,
          connectionId,
        );
        dispatch(this.retrieved(response));

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  retrieveAll(
    facebookPageId: number,
    params?: {
      pageNumber?: number;
      pageSize?: number;
      query?: string;
      queryOptions?: {
        withVariables?: boolean;
        includedNames?: 'all' | string[] | null;
        withWebhookErrors?: boolean;
      };
      type?: IntegrationTypes | 'all' | 'Webhook';
    },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));

      try {
        const response = await ConnectionsController.getAll(
          facebookPageId,
          params,
        );
        dispatch({
          type: this.retrievedAllByType,
          connectionType: params?.type,
          entities: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  retrieveWidgetConnections(
    facebookPageId: number,
    type?: 'google_spreadsheets' | 'mailchimp' | 'facebook_ads_audience',
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));

      try {
        const response = await ConnectionsController.getWidgetConnections(
          facebookPageId,
          type,
        );
        if (type === 'google_spreadsheets') {
          dispatch({
            type: this.retrievedWidgetGoogleConnections,
            connections: response.widgetWebhooks,
          });
        } else if (type === 'facebook_ads_audience') {
          dispatch({
            type: this.retrievedWidgetFacebookAudienceConnections,
            connections: response.widgetWebhooks,
          });
        } else {
          dispatch(this.retrievedWidgetConnections(response.widgetWebhooks));
        }
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  deleteConnection(facebookPageId: number, connection: ConnectionModel) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteAction));

      try {
        const response = await ConnectionsController.deleteWebhook(
          facebookPageId,
          connection.id,
        );
        await dispatch(this.deleteLocal(connection));
        return response;
      } catch (e) {
        connectionErrorHandler(dispatch)(e, facebookPageId);
      }
    };
  }

  update(facebookPageId: number, connection: Partial<ConnectionModel>) {
    return async (dispatch: Dispatch<StoreState>, getState) => {
      const { connections } = getState();

      try {
        const connectionInstance = connections.entities.find(
          item => item.id === connection.id,
        );
        const nextConnection = { ...connectionInstance, ...connection };

        dispatch(this.busy(this.createAction));
        const response = await ConnectionsController.update(
          facebookPageId,
          nextConnection,
        );
        dispatch(this.retrieved(response));

        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrievedWidgetConnections(connections: ConnectionModel[]) {
    return {
      type: this.retrieveWidgetConnectionsAction,
      connections,
    };
  }

  invalidateAll() {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.invalidateAllAction });
    };
  }

  updateVariableOptions(options) {
    return {
      type: this.updateVariableOptionsAction,
      options,
    };
  }

  retriveAddedCrms(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await ConnectionsController.getActivatedCrms(
          facebookPageId,
        );
        return response;
      } catch (e) {
        connectionErrorHandler(dispatch)(e, facebookPageId);
      }
    };
  }

  retriveCrmsLeadsSent(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await ConnectionsController.getCrmsLeadsSent(
          facebookPageId,
        );
        dispatch({
          type: this.retriveAddedCrmsLeadsSent,
          crmsLeadsSent: response,
        });
        return response;
      } catch (e) {
        connectionErrorHandler(dispatch)(e, facebookPageId);
      }
    };
  }

  retriveWebhookJsonTemplates() {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await ConnectionsController.getWebhookJsonTemplates();
        dispatch({
          type: this.retriveWebhookJsonTemplatesAction,
          webhookJsonTemplates: response,
        });
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }
}

const connectionsCRUDActions = new ConnectionsCRUDActions();

export default connectionsCRUDActions;

export const useConnectionCRUDActions = getActionHookBuilder(
  connectionsCRUDActions,
  [
    'create',
    'clone',
    'deleteConnection',
    'retrieveAll',
    'retrieveById',
    'update',
    'retrieveWidgetConnections',
    'retriveAddedCrms',
    'retriveCrmsLeadsSent',
    'retriveWebhookJsonTemplates',
    'invalidateAll',
  ],
);
